/**
 * Educational Web Image Service for Professor X AI
 * 
 * Strict semantic validation, context-aware query generation,
 * candidate ranking, species & body system mismatch rejection,
 * and deterministic image selection.
 */

import { isValidRenderableImageUrl } from './visualUtils';

export interface StaticImage {
  url: string;
  title: string;
  score: number;
  source: 'WIKIMEDIA' | 'WIKIPEDIA' | 'GOOGLE';
  description?: string;
}

// In-memory cache to prevent duplicate network calls across steps
const imageSearchCache = new Map<string, StaticImage | null>();

/**
 * Clean up raw query strings generated by AI prompts
 */
function cleanSearchQuery(rawQuery: string): string {
  if (!rawQuery) return '';
  return rawQuery
    .replace(/^simple educational diagram of\s+/i, '')
    .replace(/^educational diagram of\s+/i, '')
    .replace(/^diagram of\s+/i, '')
    .replace(/^illustration of\s+/i, '')
    .replace(/^photo of\s+/i, '')
    .replace(/^explain the\s+/i, '')
    .replace(/^explain\s+/i, '')
    .replace(/^structure of\s+/i, '')
    .replace(/^anatomy of\s+/i, '')
    .replace(/,?\s*clearly labeled.*$/i, '')
    .replace(/,?\s*textbook style.*$/i, '')
    .replace(/,?\s*clean background.*$/i, '')
    .trim();
}

/**
 * Generate context-aware search queries based on the core educational subject & topic
 */
export function generateContextAwareQueries(query: string, keywords: string[] = []): string[] {
  const cleaned = cleanSearchQuery(query);
  if (!cleaned) return [];

  const lower = cleaned.toLowerCase();

  // 1. Digestive system targeting
  if (lower.includes("digestive") || lower.includes("digestion") || lower.includes("alimentary")) {
    return [
      "human digestive system labeled diagram educational",
      "human digestive tract labeled anatomy diagram",
      "human alimentary canal labeled diagram",
      "human digestive system diagram"
    ];
  }

  // 2. Heart / Circulatory system targeting
  if (lower.includes("heart") || lower.includes("cardiac")) {
    return [
      "human heart labeled structure diagram educational",
      "human heart anatomy cross section labeled",
      "human heart chambers valves labeled diagram"
    ];
  }

  // 3. Brain / Nervous system / Neuron
  if (lower.includes("brain") || lower.includes("neuron") || lower.includes("nervous")) {
    return [
      "human neuron structure labeled diagram educational",
      "human brain anatomy labeled diagram educational",
      "human nervous system labeled diagram"
    ];
  }

  // 4. Kidney / Nephron / Excretory
  if (lower.includes("kidney") || lower.includes("nephron") || lower.includes("excretory") || lower.includes("urinary")) {
    return [
      "human kidney nephron structure labeled diagram",
      "human urinary system labeled diagram educational"
    ];
  }

  // 5. Respiratory / Lungs
  if (lower.includes("respiratory") || lower.includes("lung") || lower.includes("alveoli")) {
    return [
      "human respiratory system labeled diagram educational",
      "human lungs alveoli structure labeled diagram"
    ];
  }

  // 6. Cell / Organelles / Plant or Animal Cell
  if (lower.includes("cell") || lower.includes("mitochondria") || lower.includes("chloroplast")) {
    return [
      `${cleaned} labeled diagram educational`,
      `${cleaned} structure labeled diagram`
    ];
  }

  // Generic fallback with educational & labeled modifiers
  const result = [
    `${cleaned} labeled diagram educational`,
    `${cleaned} structure labeled anatomy diagram`,
    `${cleaned} cross section labeled diagram`,
    `${cleaned} diagram`
  ];

  if (keywords.length > 0) {
    result.unshift(`${cleaned} ${keywords.slice(0, 2).join(' ')} labeled diagram`);
  }

  return result;
}

// Unrelated animal/organism keywords that MUST NOT match human/general biology requests
const UNRELATED_SPECIES_KEYWORDS = [
  'bird', 'avian', 'pigeon', 'chicken', 'duck', 'eagle', 'owl', 'crow', 'falcon', 'parrot',
  'reptile', 'snake', 'lizard', 'turtle', 'alligator', 'crocodile', 'amphibian', 'frog', 'toad',
  'fish', 'shark', 'salmon', 'trout', 'insect', 'fly', 'bee', 'beetle', 'ant', 'grasshopper',
  'cat', 'feline', 'dog', 'canine', 'rat', 'mouse', 'rodent', 'cow', 'bovine', 'pig', 'swine',
  'horse', 'equine', 'rabbit', 'bat', 'whale', 'dolphin', 'spider', 'mollusk', 'earthworm'
];

// Unrelated human body systems
const OTHER_BODY_SYSTEMS = [
  'respiratory system', 'circulatory system', 'nervous system', 'skeletal system',
  'muscular system', 'urinary system', 'reproductive system', 'lymphatic system', 'endocrine system'
];

/**
 * Score and semantically validate a candidate image against the query concept.
 * Returns { score, rejectReason }
 */
export function validateCandidateSemanticRelevance(
  title: string,
  url: string,
  cleanedQuery: string,
  keywords: string[] = []
): { score: number; rejectReason?: string } {
  const titleLower = title.toLowerCase();
  const urlLower = url.toLowerCase();
  const queryLower = cleanedQuery.toLowerCase();

  // 1. Rejection for stock noise / unrelated graphics
  const negativeStock = ['stock photo', 'portrait', 'selfie', 'wallpaper', 'logo', 'flag', 'stamp', 'coin', 'shutterstock', 'gettyimages'];
  for (const neg of negativeStock) {
    if (titleLower.includes(neg) || urlLower.includes(neg)) {
      return { score: -200, rejectReason: `Negative stock keyword: '${neg}'` };
    }
  }

  // 2. Species Mismatch Check:
  // If query is about human anatomy or general concept without animal specified
  const isHumanOrGeneralQuery = queryLower.includes("human") || 
    queryLower.includes("digestive") || 
    queryLower.includes("heart") || 
    queryLower.includes("brain") || 
    queryLower.includes("eye") || 
    queryLower.includes("kidney") || 
    queryLower.includes("respiratory") || 
    queryLower.includes("circulatory") || 
    queryLower.includes("nervous") || 
    queryLower.includes("cell") || 
    queryLower.includes("anatomy");

  if (isHumanOrGeneralQuery) {
    for (const species of UNRELATED_SPECIES_KEYWORDS) {
      if ((titleLower.includes(species) || urlLower.includes(species)) && !queryLower.includes(species)) {
        return { score: -500, rejectReason: `Unrelated organism/species matched: '${species}'` };
      }
    }
  }

  // 3. Anatomical Body System Mismatch Check:
  // If query specifically asks for "digestive" / "digestion", reject other human systems
  if (queryLower.includes("digestive") || queryLower.includes("digestion") || queryLower.includes("alimentary")) {
    for (const otherSystem of OTHER_BODY_SYSTEMS) {
      if (titleLower.includes(otherSystem) && !queryLower.includes(otherSystem)) {
        return { score: -400, rejectReason: `Unrelated body system matched: '${otherSystem}'` };
      }
    }

    // Must contain at least one digestive term
    const digestiveTerms = ['digestive', 'digestion', 'gastrointestinal', 'alimentary', 'gut', 'stomach', 'intestine', 'esophagus', 'colon', 'rectum', 'liver', 'pancreas', 'villi', 'pharynx', 'duodenum'];
    const hasDigestiveTerm = digestiveTerms.some(term => titleLower.includes(term) || urlLower.includes(term));
    if (!hasDigestiveTerm) {
      return { score: -300, rejectReason: "Missing required digestive concept keywords" };
    }
  }

  // 4. Heart Specificity
  if (queryLower.includes("heart") || queryLower.includes("cardiac")) {
    const heartTerms = ['heart', 'cardiac', 'coronary', 'myocardium', 'ventricle', 'atrium', 'aorta', 'valve'];
    if (!heartTerms.some(term => titleLower.includes(term) || urlLower.includes(term))) {
      return { score: -300, rejectReason: "Missing required heart concept keywords" };
    }
  }

  // 5. Brain / Neuron Specificity
  if (queryLower.includes("neuron") || queryLower.includes("nerve cell")) {
    const neuronTerms = ['neuron', 'nerve cell', 'dendrite', 'axon', 'synapse', 'myelin'];
    if (!neuronTerms.some(term => titleLower.includes(term) || urlLower.includes(term))) {
      return { score: -300, rejectReason: "Missing required neuron concept keywords" };
    }
  }

  let score = 30; // Base score

  // 6. Positive Educational Signals
  const labelKeywords = ['labeled', 'labelled', 'annotated', 'labels', 'annotation', 'legend'];
  if (labelKeywords.some(kw => titleLower.includes(kw))) {
    score += 40;
  }

  const diagramKeywords = ['diagram', 'anatomy', 'structure', 'cross-section', 'schematic', 'illustration', 'vector', 'chart'];
  if (diagramKeywords.some(kw => titleLower.includes(kw))) {
    score += 30;
  }

  if (urlLower.endsWith('.svg') || titleLower.includes('svg')) {
    score += 25;
  }

  // Query word matches
  const queryWords = queryLower.split(/\s+/).filter(w => w.length > 2 && !['human', 'diagram', 'the', 'and', 'for', 'explain'].includes(w));
  for (const word of queryWords) {
    if (titleLower.includes(word)) {
      score += 15;
    }
  }

  // Keywords match bonus
  for (const kw of keywords) {
    if (kw && kw.length > 2 && titleLower.includes(kw.toLowerCase())) {
      score += 5;
    }
  }

  // Penalize non-diagram photos without label or diagram keywords
  const isImageFile = /\.(jpg|jpeg|png|webp)$/i.test(urlLower);
  if (isImageFile && !labelKeywords.some(kw => titleLower.includes(kw)) && !diagramKeywords.some(kw => titleLower.includes(kw))) {
    score -= 30;
  }

  if (score < 70) {
    return { score, rejectReason: `Score (${score}) below confidence threshold (70)` };
  }

  return { score };
}

/**
 * Search Wikimedia Commons for high quality educational diagrams
 */
async function searchWikimediaCommons(
  rawQuery: string,
  keywords: string[] = [],
  excludeUrls: Set<string> = new Set()
): Promise<StaticImage | null> {
  try {
    const searchQueries = generateContextAwareQueries(rawQuery, keywords);
    if (searchQueries.length === 0) return null;

    const candidates: Array<{ url: string; title: string; score: number }> = [];

    for (const term of searchQueries) {
      console.log(`[Visual Search] Query term: "${term}"`);
      const endpoint = `https://commons.wikimedia.org/w/api.php?action=query&generator=search&gsrsearch=${encodeURIComponent(
        term
      )}&gsrnamespace=6&gsrlimit=8&prop=imageinfo&iiprop=url|mime|extmetadata&iiurlwidth=1000&format=json&origin=*`;

      const response = await fetch(endpoint, { credentials: 'omit' });
      if (!response.ok) continue;

      const data = await response.json();
      const pages = data?.query?.pages;
      if (!pages) continue;

      for (const pageId of Object.keys(pages)) {
        const page = pages[pageId];
        const imageInfo = page?.imageinfo?.[0];
        if (!imageInfo) continue;

        const rawUrl = (imageInfo.url as string) || '';
        const mime = ((imageInfo.mime as string) || '').toLowerCase();
        const rawTitle = (page.title || '').replace(/^File:/i, '').replace(/_/g, ' ');

        let renderableUrl = rawUrl;
        const isPdf = mime === 'application/pdf' || 
          rawUrl.toLowerCase().includes('.pdf') || 
          rawTitle.toLowerCase().endsWith('.pdf');

        // CASE 1: If it's a PDF, check if an actual rendered page image (thumbnail) is available
        if (isPdf) {
          const thumb = imageInfo.thumburl as string | undefined;
          if (thumb && isValidRenderableImageUrl(thumb)) {
            // Render the actual page-rendered image from the PDF
            renderableUrl = thumb;
          } else {
            // CASE 2: PDF reference without an actual renderable image asset - do not pass raw PDF
            continue;
          }
        }

        // Verify the candidate image URL is an actual valid renderable image asset
        if (!isValidRenderableImageUrl(renderableUrl)) {
          continue;
        }

        if (excludeUrls.has(renderableUrl)) continue;

        // Clean up title: remove trailing file extensions (.pdf, .svg, .png, etc.) so filenames aren't shown
        const cleanTitle = rawTitle.replace(/\.(pdf|svg|png|jpe?g|webp)$/i, '').trim();

        const validation = validateCandidateSemanticRelevance(cleanTitle, renderableUrl, rawQuery, keywords);
        if (validation.score >= 70) {
          console.log(`[Visual Search] Candidate "${cleanTitle}" | Score: ${validation.score} | ACCEPTED`);
          candidates.push({ url: renderableUrl, title: cleanTitle, score: validation.score });
        } else {
          console.log(`[Visual Search] Candidate "${cleanTitle}" | Score: ${validation.score} | REJECTED: ${validation.rejectReason}`);
        }
      }

      if (candidates.some(c => c.score >= 85)) {
        break; // Stop early if top-tier candidate found
      }
    }

    if (candidates.length === 0) return null;

    candidates.sort((a, b) => b.score - a.score);
    const best = candidates[0];

    return {
      url: best.url,
      title: best.title,
      score: best.score,
      source: 'WIKIMEDIA'
    };
  } catch (error) {
    console.error('[Visual Search] Wikimedia Commons search error:', error);
    return null;
  }
}

/**
 * Search Wikipedia REST API as secondary fallback
 */
async function searchWikipediaREST(
  rawQuery: string,
  keywords: string[] = [],
  excludeUrls: Set<string> = new Set()
): Promise<StaticImage | null> {
  try {
    const cleaned = cleanSearchQuery(rawQuery);
    if (!cleaned) return null;

    const term = `${cleaned} diagram`;
    console.log(`[Visual Search] Fallback Wikipedia Query: "${term}"`);

    const endpoint = `https://en.wikipedia.org/w/api.php?action=query&generator=search&gsrsearch=${encodeURIComponent(
      term
    )}&gsrlimit=5&prop=pageimages|extracts&piprop=original|thumbnail&pithumbsize=1000&format=json&origin=*`;

    const response = await fetch(endpoint, { credentials: 'omit' });
    if (!response.ok) return null;

    const data = await response.json();
    const pages = data?.query?.pages;
    if (!pages) return null;

    for (const pageId of Object.keys(pages)) {
      const page = pages[pageId];
      const imgUrl = page?.original?.source || page?.thumbnail?.source;
      if (imgUrl && isValidRenderableImageUrl(imgUrl) && !excludeUrls.has(imgUrl)) {
        const title = (page.title || cleaned).replace(/\.(pdf|svg|png|jpe?g|webp)$/i, '').trim();
        const validation = validateCandidateSemanticRelevance(title, imgUrl, rawQuery, keywords);
        if (validation.score >= 70) {
          console.log(`[Visual Search Wikipedia] Candidate "${title}" | Score: ${validation.score} | ACCEPTED`);
          return {
            url: imgUrl,
            title,
            score: validation.score,
            source: 'WIKIPEDIA'
          };
        } else {
          console.log(`[Visual Search Wikipedia] Candidate "${title}" | Score: ${validation.score} | REJECTED: ${validation.rejectReason}`);
        }
      }
    }
    return null;
  } catch (error) {
    console.error('[Visual Search] Wikipedia REST search error:', error);
    return null;
  }
}

/**
 * Main public entry point for fetching high quality educational web images.
 * Strict threshold: Returns null if no candidate passes semantic validation (>= 70).
 */
export async function fetchStaticImage(
  query: string,
  keywords: string[] = [],
  excludeUrls: Set<string> = new Set()
): Promise<StaticImage | null> {
  if (!query || typeof query !== 'string' || !query.trim()) return null;

  console.log(`[Visual Search] Initiating educational visual retrieval for: "${query}"`);

  const cacheKey = `${query.trim().toLowerCase()}_${(keywords || []).join(',')}_${excludeUrls.size}`;
  if (imageSearchCache.has(cacheKey)) {
    const cached = imageSearchCache.get(cacheKey) || null;
    console.log(`[Visual Search] Cache hit for query: "${query}" -> ${cached ? cached.title : 'null'}`);
    return cached;
  }

  // 1. Search Wikimedia Commons
  let result = await searchWikimediaCommons(query, keywords, excludeUrls);

  // 2. Fallback to Wikipedia REST API if needed
  if (!result || result.score < 70) {
    const wikiResult = await searchWikipediaREST(query, keywords, excludeUrls);
    if (wikiResult && wikiResult.score >= 70) {
      result = wikiResult;
    }
  }

  if (result && result.score < 70) {
    console.log(`[Visual Search] Final result rejected (score ${result.score} < 70 threshold). Displaying no image.`);
    result = null;
  }

  if (result) {
    console.log(`[Visual Search] SUCCESS: Selected image "${result.title}" from ${result.source} (Score: ${result.score})`);
  } else {
    console.log(`[Visual Search] NO VALID IMAGE FOUND for "${query}". Preserving clean visual panel.`);
  }

  imageSearchCache.set(cacheKey, result);
  return result;
}

/**
 * Automatically resolves educational web images for lesson static_image_query and step-level image_query.
 */
export async function resolveStepAndLessonImages<T extends { static_image_query?: string; static_image_url?: string; image_keywords?: string[]; steps: any[] }>(
  data: T
): Promise<T> {
  if (!data) return data;

  const usedUrls = new Set<string>();

  try {
    // Sanitize any pre-existing lesson-level image
    if (data.static_image_url && !isValidRenderableImageUrl(data.static_image_url)) {
      data.static_image_url = undefined;
    }

    // 1. Resolve lesson-level static image if requested
    if (data.static_image_query && !data.static_image_url) {
      const lessonImg = await fetchStaticImage(data.static_image_query, data.image_keywords || [], usedUrls);
      if (lessonImg && isValidRenderableImageUrl(lessonImg.url)) {
        data.static_image_url = lessonImg.url;
        usedUrls.add(lessonImg.url);
      } else {
        data.static_image_url = undefined;
      }
    }

    // 2. Resolve step-level images sequentially to preserve usedUrls context & prevent duplicates
    if (Array.isArray(data.steps)) {
      for (const step of data.steps) {
        // Clear invalid pre-existing image references
        if (step.image_url && !isValidRenderableImageUrl(step.image_url)) {
          step.image_url = undefined;
        }
        if (step.static_image_url && !isValidRenderableImageUrl(step.static_image_url)) {
          step.static_image_url = undefined;
        }

        const query = step.image_query || step.static_image_query;
        if (query && typeof query === 'string' && query.trim() && !step.diagram_svg && !step.image_url) {
          const stepImg = await fetchStaticImage(query, data.image_keywords || [], usedUrls);
          if (stepImg && isValidRenderableImageUrl(stepImg.url)) {
            step.image_url = stepImg.url;
            step.static_image_url = stepImg.url;
            usedUrls.add(stepImg.url);
            if (!step.diagram_description) {
              step.diagram_description = stepImg.title;
            }
          } else {
            step.image_url = undefined;
            step.static_image_url = undefined;
          }
        }
      }
    }
  } catch (err) {
    console.error('[Visual Search] Error resolving images for lesson:', err);
  }

  return data;
}
